import { GridFiltersController, MARKETERS, useCompanySegmentFilterCascaded, useFilterItemQuery } from "@repo/baseCloud";
import { type FilterItem, KeyedArray } from "@repo/models";
import { DropDownList } from "@repo/ui";
import { useMemo } from "react";

import type { LayerFilterProps } from "../hooks/useUpdateLayerFilter";

import { useVerbiageQuery } from "../../../api/verbiageApi";
import { MarketersDropdown } from "../../../components/segmentFilters";
import { DivisionsDropdown, MarketsDropdown, RegionsDropdown } from "../../../components/segmentFilters/companySegment";
import { useBranches, useLocationStatuses, usePartners, usePartnerTypes, useUsers } from "../hooks/useLayerLookups";
import { ActiveStatusDropdown } from "./activeStatusDropdown";
import { ClientSideLabel } from "./clientSideLabel";

const UNASSIGNED: FilterItem = { id: 0, name: "Unassigned", value: "Unassigned" };

export function RetailLocationsFilters({ layer, updateFilter }: LayerFilterProps) {
	const companySegmentQuery = useCompanySegmentFilterCascaded(layer);
	const { verbiage } = useVerbiageQuery();
	const [locationStatuses, locationStatusesLoaded] = useLocationStatuses();
	const [partnerTypes, partnerTypesLoaded] = usePartnerTypes();
	const [partners, partnersLoaded] = usePartners(layer.partnerTypeId);
	const [users, usersLoaded] = useUsers();
	const [marketers, marketersLoaded] = useFilterItemQuery([MARKETERS], GridFiltersController.getMarketers);
	const [branches, branchesLoaded] = useBranches();

	const marketerOptions = useMemo(
		() => new KeyedArray([UNASSIGNED, ...marketers.data], (marketer) => marketer.id),
		[marketers],
	);

	const offices = useMemo(() => {
		const scoped = layer.marketerIds?.length
			? branches.data.filter((branch) => layer.marketerIds.includes(branch.marketerId))
			: branches.data;
		return [UNASSIGNED, ...scoped];
	}, [branches, layer.marketerIds]);

	return (
		<>
			<DropDownList
				label={<ClientSideLabel label="Status" />}
				placeholder="All Statuses"
				value={layer.statusIds ?? []}
				changeHandler={(value) => updateFilter(value, "statusIds")}
				values={locationStatuses.data}
				valueProp="id"
				textProp="name"
				fullLength
				clearable
				loaded={locationStatusesLoaded}
				isMulti
			/>
			<ActiveStatusDropdown layer={layer} updateFilter={updateFilter} label="Active / Inactive" />
			<DropDownList
				id="partnerTypeId"
				label={<ClientSideLabel label={verbiage.partnerType} />}
				placeholder={`All ${verbiage.partnerType}s`}
				value={layer.partnerTypeId ?? ""}
				changeHandler={updateFilter}
				values={partnerTypes.data}
				valueProp="id"
				textProp="value"
				fullLength
				loaded={partnerTypesLoaded}
				clearable
			/>
			<DropDownList
				id="partnerId"
				label={<ClientSideLabel label={verbiage.partner} />}
				placeholder={`All ${verbiage.partner}s`}
				value={layer.partnerId ?? ""}
				changeHandler={updateFilter}
				values={partners.data}
				valueProp="id"
				textProp="value"
				fullLength
				loaded={partnersLoaded}
				clearable
			/>
			<DivisionsDropdown
				label={<ClientSideLabel label="Division" />}
				options={companySegmentQuery.divisions}
				loaded={companySegmentQuery.divisionsLoaded}
				value={layer.companyDivisionIds ?? []}
				changeHandler={(value) => updateFilter(value, "companyDivisionIds")}
				maxSelectedDisplay={3}
			/>
			<RegionsDropdown
				label={<ClientSideLabel label="Region" />}
				options={companySegmentQuery.regions}
				loaded={companySegmentQuery.regionsLoaded}
				value={layer.companyRegionIds ?? []}
				changeHandler={(value) => updateFilter(value, "companyRegionIds")}
				maxSelectedDisplay={3}
			/>
			<MarketsDropdown
				label={<ClientSideLabel label="Market" />}
				options={companySegmentQuery.markets}
				loaded={companySegmentQuery.marketsLoaded}
				value={layer.companyMarketIds ?? []}
				changeHandler={(value) => updateFilter(value, "companyMarketIds")}
				maxSelectedDisplay={3}
			/>
			<DropDownList
				label="Assigned AE"
				placeholder="All Assigned AEs"
				id="assignedUserIds"
				value={layer.assignedUserIds ?? []}
				changeHandler={(value) => updateFilter(value, "assignedUserIds")}
				values={users.data}
				valueProp="id"
				textProp="value"
				fullLength
				loaded={usersLoaded}
				clearable
				isMulti
			/>
			<MarketersDropdown
				label={`Assigned ${verbiage.marketer}`}
				options={marketerOptions}
				loaded={marketersLoaded}
				value={layer.marketerIds ?? []}
				changeHandler={updateFilter}
				maxSelectedDisplay={3}
				allOption={false}
			/>
			<DropDownList
				label={`Assigned ${verbiage.office}`}
				placeholder={`All Assigned ${verbiage.office}s`}
				value={layer.assignedOfficeIds ?? []}
				id="assignedOfficeIds"
				changeHandler={updateFilter}
				values={offices}
				valueProp="id"
				textProp="value"
				fullLength
				loaded={branchesLoaded}
				clearable
				isMulti
			/>
		</>
	);
}
